// src/features/reit-info/ReitInfoPage.tsx
//
// REIT Info Deep-Dive Page — Component Islands architecture for progressive
// loading and failure isolation. Mirrors StockInfoPage / EtfInfoPage but
// through the Real-Estate-native lens: Dividend Yield, Payout Ratio,
// Price/FFO proxy, a Dividend History track record, Leverage & Safety, and
// competitor REITs ranked by market cap.
//
// Extracts the symbol from the URL query param (?symbol=O), fetches the
// Overview island (quote + profile + TTM yield ratios) up front, and lets
// each downstream island (dividends, safety, chart, peers) fetch its own data
// independently so one slow/failing endpoint never blocks the page.

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout, Sidebar } from '../../shared/ui';
import { fetchReitOverview, type ReitOverview } from './api';
import { IntradayChart } from './components/IntradayChart';
import { DividendHistoryChart } from './components/DividendHistoryChart';
import { SafetyMetricsTable } from './components/SafetyMetricsTable';
import { ReitPeersTable } from './components/ReitPeersTable';
import { usePortfolioStore } from '../user-portfolios/store/usePortfolioStore';

export const ReitInfoPage = () => {
    const navigate = useNavigate();
    // Zustand: opens the global "Add to Portfolio" modal (isolated re-render).
    const openModal = usePortfolioStore((s) => s.openModal);

    const [searchParams] = useSearchParams();
    const symbol = searchParams.get('symbol')?.toUpperCase();

    const [overview, setOverview] = useState<ReitOverview | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!symbol) return;

        let cancelled = false;
        setIsLoading(true);
        setError(null);

        fetchReitOverview(symbol)
            .then((data) => {
                if (!cancelled) setOverview(data);
            })
            .catch((err) => {
                if (!cancelled) {
                    // eslint-disable-next-line no-console
                    console.error(err);
                    setError(`Unable to load data for ${symbol}. Please try again.`);
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [symbol]);

    // Formatters for the institutional look.
    const formatCurrency = (val?: number) =>
        val != null
            ? new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
              }).format(val)
            : 'N/A';
    const formatCompact = (val?: number) =>
        val != null
            ? new Intl.NumberFormat('en-US', {
                  notation: 'compact',
                  maximumFractionDigits: 2,
              }).format(val)
            : 'N/A';
    const formatPercent = (val?: number) =>
        val != null ? `${val > 0 ? '+' : ''}${val.toFixed(2)}%` : 'N/A';
    // Yield & payout arrive as fractions (0.0512 = 5.12%).
    const formatFractionPct = (val?: number, digits = 2) =>
        val != null ? `${(val * 100).toFixed(digits)}%` : 'N/A';

    if (!symbol) {
        return (
            <Layout sidebar={<Sidebar />} title="REIT Intelligence">
                <div className="p-6 text-slate-500 dark:text-slate-400">
                    No symbol provided. Please select a REIT from the Screener.
                </div>
            </Layout>
        );
    }

    const reitName =
        overview?.profile?.company_name ||
        overview?.quote?.name ||
        symbol;

    return (
        <Layout
            sidebar={<Sidebar />}
            title={`REIT Deep Dive: ${symbol}`}
            subtitle={reitName}
        >
            <div className="mb-6">
                <button
                    onClick={() => navigate(-1)}
                    className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                >
                    &larr; Back to Rankings
                </button>
            </div>

            {error && (
                <div className="mb-6 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-400">
                    {error}
                </div>
            )}

            {isLoading ? (
                <div className="h-64 flex items-center justify-center text-slate-500 dark:text-slate-400 animate-pulse">
                    Loading REIT data for {symbol}…
                </div>
            ) : (
                overview && (
                    <div className="space-y-6">
                        {/* --- HEADER ISLAND: Identity & Live Quote --- */}
                        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 border-l-4 border-l-emerald-500">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                                <div>
                                    <div className="flex items-center gap-3 mb-1">
                                        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
                                            {reitName}
                                        </h1>
                                        <span className="px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-mono font-bold rounded border border-slate-200 dark:border-slate-700">
                                            {overview.quote.exchange || 'N/A'} : {symbol}
                                        </span>
                                    </div>
                                    <p className="text-slate-500 dark:text-slate-400 text-sm">
                                        {[
                                            overview.profile.sector,
                                            overview.profile.industry,
                                        ]
                                            .filter(Boolean)
                                            .join(' • ') || 'Real Estate Investment Trust'}
                                    </p>
                                </div>

                                <div className="mt-4 md:mt-0 text-left md:text-right">
                                    <div className="text-4xl font-mono font-bold text-slate-900 dark:text-white">
                                        {formatCurrency(overview.quote.price)}
                                    </div>
                                    <div
                                        className={`text-lg font-medium mt-1 flex items-center md:justify-end gap-1 ${
                                            (overview.quote.change ?? 0) >= 0
                                                ? 'text-emerald-600 dark:text-emerald-400'
                                                : 'text-red-600 dark:text-red-400'
                                        }`}
                                    >
                                        {(overview.quote.change ?? 0) >= 0 ? '▲' : '▼'}
                                        {Math.abs(overview.quote.change ?? 0).toFixed(2)} (
                                        {formatPercent(overview.quote.changes_percentage)})
                                    </div>
                                    <button
                                        onClick={() =>
                                            openModal({
                                                symbol,
                                                name: reitName,
                                                type: 'REIT',
                                                sector: overview.profile.sector ?? null,
                                                currentPrice: overview.quote.price ?? 0,
                                            })
                                        }
                                        className="mt-3 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 transition"
                                    >
                                        + Add to Portfolio
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* --- KPI WIDGETS (REIT-native yield lens) --- */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
                                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                                    Dividend Yield
                                </p>
                                <div className="text-2xl font-bold mt-1 text-emerald-600 dark:text-emerald-400">
                                    {formatFractionPct(overview.yield_metrics.dividend_yield)}
                                </div>
                            </div>

                            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
                                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                                    Payout Ratio
                                </p>
                                <div className="text-2xl font-bold mt-1 text-slate-900 dark:text-white">
                                    {formatFractionPct(overview.yield_metrics.payout_ratio, 1)}
                                </div>
                            </div>

                            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
                                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                                    Price / FFO
                                </p>
                                <div className="text-2xl font-bold mt-1 text-slate-900 dark:text-white">
                                    {overview.price_to_ffo != null
                                        ? `${overview.price_to_ffo.toFixed(2)}×`
                                        : 'N/A'}
                                </div>
                                <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">
                                    P/OCF proxy
                                </p>
                            </div>

                            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
                                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                                    Market Cap
                                </p>
                                <div className="text-2xl font-bold mt-1 text-amber-600 dark:text-amber-500">
                                    ${formatCompact(overview.quote.market_cap)}
                                </div>
                            </div>
                        </div>

                        {/* --- REIT SNAPSHOT (full-width, below the KPIs) --- */}
                        <div className="surface-card flex flex-col">
                            <h2 className="section-title mb-4">REIT Snapshot</h2>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                                <div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                                        Price / Book
                                    </p>
                                    <p className="text-lg font-bold text-slate-900 dark:text-white mt-1">
                                        {overview.yield_metrics.price_to_book != null
                                            ? `${overview.yield_metrics.price_to_book.toFixed(2)}×`
                                            : 'N/A'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                                        Beta
                                    </p>
                                    <p className="text-lg font-bold text-slate-900 dark:text-white mt-1">
                                        {overview.profile.beta != null
                                            ? overview.profile.beta.toFixed(2)
                                            : 'N/A'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                                        Sector
                                    </p>
                                    <p className="text-lg font-bold text-slate-900 dark:text-white mt-1">
                                        {overview.profile.sector || 'N/A'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                                        Sub-Industry
                                    </p>
                                    <p className="text-lg font-bold text-slate-900 dark:text-white mt-1">
                                        {overview.profile.industry || 'N/A'}
                                    </p>
                                </div>
                            </div>
                            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                                {overview.profile.description ||
                                    'No REIT description available.'}
                            </p>
                        </div>

                        {/* --- DIVIDEND HISTORY & LEVERAGE/SAFETY --- */}
                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-stretch">
                            {/* Dividend chart gets 2/3 width on large screens */}
                            <div className="xl:col-span-2">
                                <DividendHistoryChart symbol={symbol} />
                            </div>

                            {/* Safety table gets 1/3 width */}
                            <div className="xl:col-span-1">
                                <SafetyMetricsTable symbol={symbol} />
                            </div>
                        </div>

                        {/* --- INTERACTIVE CHART & PEERS GRID --- */}
                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-stretch">
                            {/* Chart gets 2/3 width on large screens */}
                            <div className="xl:col-span-2">
                                <IntradayChart symbol={symbol} />
                            </div>

                            {/* Peers gets 1/3 width */}
                            <div className="xl:col-span-1 h-[440px] xl:h-auto">
                                <ReitPeersTable symbol={symbol} />
                            </div>
                        </div>
                    </div>
                )
            )}
        </Layout>
    );
};

export default ReitInfoPage;
